import React from 'react';
import { Grid, MenuItem, Select, Autocomplete, TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import ChipInput from '../../ChipInput/ChipInput';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { FileCopyOutlined } from '@mui/icons-material';
import { getColumnValue } from './DbQueryColumnUtil';
import './queryDatabase.css';

export const QueryConditionals = props => {
  const {
    index,
    queryData,
    onClick,
    aData,
    updateCondition,
    availableCols,
    validateKey,
    onPlaceholderSelected,
    availablePlaceholders,
    setThisFocus,
    deleteQueryItemCondition,
    duplicateCondition,
  } = props;

  const handleChipDelete = (aDataConditionIndex, aDataCondition) => {
    return () => {
      updateCondition(
        index,
        aDataConditionIndex,
        aDataCondition.logic,
        aDataCondition.key,
        aDataCondition.function,
        '',
        aDataCondition.datetimeValue,
        queryData
      );
    };
  };

  const handleDateOnChange = (aDataConditionIndex, aDataCondition) => {
    return newValue => {
      updateCondition(
        index,
        aDataConditionIndex,
        aDataCondition.logic,
        aDataCondition.key,
        aDataCondition.function,
        (() => {
          try {
            return newValue.toISOString();
          } catch (error) {
            return newValue ? newValue.toString() : '';
          }
        })(),
        aDataCondition.datetimeValue,
        queryData
      );
    };
  };

  const handleDateFunctionSelectOnChange = (aDataCondition, aDataConditionIndex) => {
    return event => {
      let func = aDataCondition.function;
      let value = aDataCondition.value;
      let datetimeValue = event.target.value;
      updateCondition(
        index,
        aDataConditionIndex,
        aDataCondition.logic,
        aDataCondition.key,
        func,
        value,
        datetimeValue,
        queryData
      );
    };
  };

  const handleFunctionSelectOnChange = (aDataCondition, aDataConditionIndex) => {
    return event => {
      let func = event.target.value;
      let value = aDataCondition.value;
      if (func === 'EMPTY' || func === 'NOTEMPTY') {
        value = '';
      }
      updateCondition(
        index,
        aDataConditionIndex,
        aDataCondition.logic,
        aDataCondition.key,
        func,
        value,
        aDataCondition.datetimeValue,
        queryData
      );
    };
  };

  const handleLogicOperatorOnChange = (aDataConditionIndex, aDataCondition) => {
    return event => {
      let logic = event.target.value;

      updateCondition(
        index,
        aDataConditionIndex,
        logic,
        aDataCondition.key,
        aDataCondition.function,
        aDataCondition.value,
        aDataCondition.datetimeValue,
        queryData
      );
    };
  };

  const handleColumnOnChange = (aDataCondition, aDataConditionIndex) => {
    return (event, newValue) => {
      let key = newValue ? getColumnValue(newValue) : null;
      const column = availableCols.find(col => col.placeholderKey === key);
      if (
        (aDataCondition.function === 'MATCHES' || aDataCondition.function === 'NOTMATCH') &&
        column?.type === 'DATETIME'
      ) {
        updateCondition(
          index,
          aDataConditionIndex,
          aDataCondition.logic,
          key,
          'GREATER_THAN',
          aDataCondition.value,
          aDataCondition.datetimeValue,
          queryData
        );
      } else {
        updateCondition(
          index,
          aDataConditionIndex,
          aDataCondition.logic,
          key,
          aDataCondition.function,
          aDataCondition.value,
          aDataCondition.datetimeValue,
          queryData
        );
      }
    };
  };

  return (
    <div className="section-container">
      <p className="section-title">Where the values in</p>
      {aData.condition &&
        aData.condition.map((aDataCondition, aDataConditionIndex) => {
          // Find the currently selected column object for this condition
          const selectedColumn = availableCols.find(column => {
            return getColumnValue(column) === aDataCondition.key;
          });

          return (
            <div className="condition-card" key={aDataConditionIndex}>
              {aDataConditionIndex > 0 && (
                <CloseIcon
                  className="styled-delete-icon"
                  onClick={value => deleteQueryItemCondition(index, aDataConditionIndex, queryData)}
                />
              )}
              {duplicateCondition && (
                <FileCopyOutlined
                  className={`styled-duplicate-icon ${
                    aDataConditionIndex === 0 ? '' : 'styled-duplicate-icon-with-delete'
                  }`}
                  onClick={() => duplicateCondition(index, aDataConditionIndex, queryData)}
                />
              )}
              <div className="condition-row">
                <div
                  className={`condition-controls ${
                    duplicateCondition ? 'condition-controls-with-duplicate' : ''
                  }`}
                >
                  {aDataConditionIndex > 0 && (
                    <Select
                      className="query-logic-select"
                      name="selectType"
                      disableUnderline
                      variant="standard"
                      value={aDataCondition.logic}
                      onChange={handleLogicOperatorOnChange(aDataConditionIndex, aDataCondition)}
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  )}

                  <FormControl
                    className="query-form-control"
                    error={aDataCondition.key == null ? true : false}
                  >
                    <Autocomplete
                      className="query-column-autocomplete"
                      value={selectedColumn || null}
                      onChange={handleColumnOnChange(aDataCondition, aDataConditionIndex)}
                      options={availableCols}
                      getOptionLabel={option => option.name || ''}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder="select column"
                          variant="standard"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.placeholderKey || option.realValue}>
                          {option.name}
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => {
                        return getColumnValue(option) === getColumnValue(value);
                      }}
                      autoHighlight
                      disableClearable={false}
                      noOptionsText="No columns found"
                    />
                    {aDataCondition.key == null && validateKey ? (
                      <FormHelperText>This field cannot be empty</FormHelperText>
                    ) : (
                      ''
                    )}
                  </FormControl>
                  <FormControl
                    className="query-form-control"
                    error={aDataCondition.function == null}
                  >
                    <Select
                      className="query-function-select"
                      name="selectType"
                      disableUnderline
                      variant="standard"
                      displayEmpty
                      value={aDataCondition.function}
                      onChange={handleFunctionSelectOnChange(aDataCondition, aDataConditionIndex)}
                    >
                      <MenuItem value={null} disabled>
                        Function
                      </MenuItem>
                      <MenuItem value="EQUAL">equals</MenuItem>
                      <MenuItem value="NOT_EQUAL">does not equal</MenuItem>
                      {aDataCondition.type !== 'DATETIME' && (
                        <MenuItem value="MATCHES"> contains </MenuItem>
                      )}
                      {aDataCondition.type !== 'DATETIME' && (
                        <MenuItem value="NOTMATCH"> does not contain </MenuItem>
                      )}
                      <MenuItem value="GREATER_THAN">greater than</MenuItem>
                      <MenuItem value="GREATERTHANOREQUAL">greater than or equal to</MenuItem>
                      <MenuItem value="LESS_THAN">less than</MenuItem>
                      <MenuItem value="LESSTHANOREQUAL">less than or equal to</MenuItem>
                      <MenuItem value="NOTEMPTY"> is not empty </MenuItem>
                      <MenuItem value="EMPTY"> is empty </MenuItem>
                    </Select>
                    {aDataCondition.function == null && validateKey ? (
                      <FormHelperText>This field cannot be empty</FormHelperText>
                    ) : (
                      ''
                    )}
                  </FormControl>

                  {aDataCondition.type === 'DATETIME' && (
                    <FormControl
                      className="query-form-control"
                      error={aDataCondition.function == null}
                    >
                      <Select
                        className="query-datetime-select"
                        name="selectType"
                        disableUnderline
                        variant="standard"
                        displayEmpty
                        value={aDataCondition.datetimeValue ? aDataCondition.datetimeValue : null}
                        onChange={handleDateFunctionSelectOnChange(
                          aDataCondition,
                          aDataConditionIndex
                        )}
                      >
                        <MenuItem value={null} disabled>
                          Datetime input method
                        </MenuItem>
                        <MenuItem value="datetime">A specific datetime value</MenuItem>
                        <MenuItem value="placeholder">A datetime placeholder</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </div>
              </div>

              {/* Full-width input row - separate from padded controls */}
              {aDataCondition.function !== 'EMPTY' && aDataCondition.function !== 'NOTEMPTY' && (
                <div className="input-row">
                  {aDataCondition.type === 'DATETIME' &&
                  aDataCondition.datetimeValue === 'datetime' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        className="conditions-datetime-picker"
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                        format="YYYY/MM/DD hh:mm a"
                        label="Pick a datetime value"
                        timezone={'UTC'}
                        value={aDataCondition.value ? dayjs(aDataCondition.value) : dayjs('')}
                        onChange={handleDateOnChange(aDataConditionIndex, aDataCondition)}
                      />
                    </LocalizationProvider>
                  ) : (
                    <ChipInput
                      className="chip-input"
                      onPlaceholderSelected={onPlaceholderSelected}
                      placeholders={availablePlaceholders}
                      onBeforeAdd={chip => {
                        let val = chip;
                        if (val && val.trim().length > 0) {
                          updateCondition(
                            index,
                            aDataConditionIndex,
                            aDataCondition.logic,
                            aDataCondition.key,
                            aDataCondition.function,
                            val,
                            aDataCondition.datetimeValue,
                            queryData
                          );
                          return true;
                        } else {
                          return false;
                        }
                      }}
                      placeholder={
                        aDataCondition.type === 'DATETIME'
                          ? 'Insert a datetime placeholder'
                          : 'Type something or insert a placeholder'
                      }
                      inputValue={aDataCondition.value ? [aDataCondition.value] : []}
                      inputType={aDataCondition.type}
                      onDelete={handleChipDelete(aDataConditionIndex, aDataCondition)}
                      onFocus={() => {
                        setThisFocus(index, aDataConditionIndex);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}

      <div className="add-condition-container">
        <span className="styled-span" onClick={onClick}>
          <AddIcon /> Add condition
        </span>
      </div>
    </div>
  );
};
